/**
 * Vintage Card — the workhorse surface (pure CSS-Module; Mantine's `<Card>` is
 * re-exported as `MantineCard` when its compound parts are needed).
 *
 * `variant` controls the surface treatment:
 *   elevated — hairline border + soft resting shadow (default)
 *   outlined — hairline border, no shadow (flatter; dense grids / nested-feel)
 *   flat     — raised tint surface, no border/shadow (a "well" inside a page)
 *   accent   — elevated + a left status stripe in `accentTone` (default brand)
 * `density` controls padding (24px default / 16px dense / 32px focus).
 * `interactive` adds the hover-lift + focus ring (pair with onClick + a role).
 */
import {
  forwardRef,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import styles from './Card.module.css';

type CardVariant = 'elevated' | 'outlined' | 'flat' | 'accent';
type AccentTone = 'brand' | 'gold' | 'success' | 'warning' | 'error' | 'info';

const VARIANT_CLASS: Record<CardVariant, string | undefined> = {
  elevated: undefined,
  outlined: styles.outlined,
  flat: styles.flat,
  accent: styles.accent,
};

const ACCENT_TONE_CLASS: Record<AccentTone, string | undefined> = {
  brand: styles.toneBrand,
  gold: styles.toneGold,
  success: styles.toneSuccess,
  warning: styles.toneWarning,
  error: styles.toneError,
  info: styles.toneInfo,
};

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  readonly variant?: CardVariant;
  readonly density?: 'default' | 'dense' | 'focus';
  /** Left status-stripe colour when `variant="accent"`. Default `brand`. */
  readonly accentTone?: AccentTone;
  /** Lift on hover, gain a focus ring, become clickable-feeling. */
  readonly interactive?: boolean;
  /** Render as a different element (e.g. `'article'`, `'button'`, `'a'`). */
  readonly as?: ElementType;
  readonly children?: ReactNode;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  {
    variant = 'elevated',
    density = 'default',
    accentTone = 'brand',
    interactive = false,
    as: Tag = 'div',
    className,
    children,
    ...rest
  },
  ref,
) {
  const Component = Tag;
  const classes = [
    styles.root,
    VARIANT_CLASS[variant],
    variant === 'accent' ? ACCENT_TONE_CLASS[accentTone] : undefined,
    density === 'dense' ? styles.dense : undefined,
    density === 'focus' ? styles.focus : undefined,
    interactive ? styles.interactive : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Component ref={ref} className={classes} {...rest}>
      {children}
    </Component>
  );
});
